/** Camera scanning (webcam / phone camera): EAN-13, EAN-8, UPC, Code-128, QR. */
import { useEffect, useRef, useState } from 'react';

import { Modal } from './ui';

export default function CameraScanner({ open, onClose, onScan, continuous = false }: {
  open: boolean; onClose: () => void; onScan: (code: string) => void; continuous?: boolean;
}) {
  const regionId = useRef(`cam-${Math.random().toString(36).slice(2)}`);
  const [error, setError] = useState('');
  const last = useRef({ code: '', at: 0 });

  useEffect(() => {
    if (!open) return;
    let scanner: { stop: () => Promise<void>; clear: () => void } | null = null;
    let cancelled = false;
    (async () => {
      try {
        const mod = await import('html5-qrcode');
        if (cancelled) return;
        const F = mod.Html5QrcodeSupportedFormats;
        const s = new mod.Html5Qrcode(regionId.current, {
          verbose: false,
          formatsToSupport: [F.EAN_13, F.EAN_8, F.UPC_A, F.UPC_E, F.CODE_128, F.QR_CODE],
        });
        scanner = s;
        await s.start({ facingMode: 'environment' }, { fps: 10, qrbox: { width: 300, height: 160 } }, (text) => {
          const now = Date.now();
          // The camera sees the same code many times a second; count it once.
          if (text === last.current.code && now - last.current.at < 1500) return;
          last.current = { code: text, at: now };
          onScan(text);
          if (!continuous) onClose();
        }, () => undefined);
      } catch (e) {
        setError('Camera not available. Allow camera access, or use a USB scanner / type the code.');
      }
    })();
    return () => {
      cancelled = true;
      if (scanner) scanner.stop().then(() => scanner?.clear()).catch(() => undefined);
    };
  }, [open, continuous, onClose, onScan]);

  return (
    <Modal open={open} onClose={onClose} title="Scan with camera" size="sm">
      {error ? <p className="text-red-600">{error}</p> : <p className="mb-2 text-sm text-slate-500">Hold the barcode inside the box.</p>}
      <div id={regionId.current} className="overflow-hidden rounded-lg bg-black" />
    </Modal>
  );
}
